// Starter Tape lanes — one row per instrument track, with a small preview of
// its notes across the loop, a mute toggle and (with `onClear`) a button that
// empties the lane. Selecting a lane opens it in the piano roll.

import React from "react";
import { cn } from "../../lib/utils";
import { INSTRUMENTS } from "../../lib/starterTape";

// Literal class strings so Tailwind's JIT scanner picks them up.
export const LANE_STYLES = {
  pad: {
    note: "bg-violet-400/70 border-violet-300",
    ring: "ring-violet-400/70",
    dot: "bg-violet-400",
  },
  bell: {
    note: "bg-amber-300/80 border-amber-200",
    ring: "ring-amber-300/70",
    dot: "bg-amber-300",
  },
  bass: {
    note: "bg-sky-400/70 border-sky-300",
    ring: "ring-sky-400/70",
    dot: "bg-sky-400",
  },
  drums: {
    note: "bg-rose-400/70 border-rose-300",
    ring: "ring-rose-400/70",
    dot: "bg-rose-400",
  },
};

export default function LaneList({
  tracks,
  steps,
  selectedId,
  onSelect,
  onToggleMute,
  onClear = null,
  registerPlayhead,
}) {
  const pct = (n) => `${(n / steps) * 100}%`;

  return (
    <div className="space-y-2">
      {tracks.map((track) => {
        const inst = INSTRUMENTS[track.instrument];
        const style = LANE_STYLES[track.instrument];
        const selected = track.id === selectedId;
        // Map each note's pitch onto the lane's height for a mini preview.
        const span = inst.high - inst.low || 1;
        return (
          <div
            key={track.id}
            className={cn(
              "flex items-stretch gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-2",
              selected && `ring-2 ${style.ring}`
            )}
          >
            <button
              type="button"
              onClick={() => onSelect(track.id)}
              className="flex w-28 shrink-0 flex-col items-start justify-center rounded-lg px-2 text-left hover:bg-zinc-800/60"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
                <span className={cn("h-2 w-2 rounded-full", style.dot)} />
                {inst.name}
              </span>
              <span className="text-xs text-zinc-500">
                {selected ? "editing" : "click to edit"}
              </span>
            </button>
            <button
              type="button"
              onClick={() => onSelect(track.id)}
              aria-label={`Edit ${inst.name} lane`}
              className={cn(
                "relative h-12 flex-1 overflow-hidden rounded-lg bg-zinc-950/60",
                track.muted && "opacity-40"
              )}
            >
              {track.notes.map((n) => (
                <span
                  key={`${n.pitch}-${n.step}`}
                  className={cn("absolute h-1.5 rounded-full", style.dot)}
                  style={{
                    left: pct(n.step),
                    width: pct(n.length),
                    top: `${8 + (1 - (n.pitch - inst.low) / span) * 30}px`,
                  }}
                />
              ))}
              <span
                ref={registerPlayhead}
                className="pointer-events-none absolute top-0 bottom-0 w-px bg-zinc-100/70"
                style={{ display: "none" }}
              />
            </button>
            <button
              type="button"
              onClick={() => onToggleMute(track.id)}
              aria-pressed={track.muted}
              className={cn(
                "w-14 shrink-0 rounded-lg border text-xs font-medium",
                track.muted
                  ? "border-zinc-600 bg-zinc-800 text-zinc-300"
                  : "border-zinc-800 text-zinc-500 hover:text-zinc-300"
              )}
            >
              {track.muted ? "muted" : "mute"}
            </button>
            {onClear && (
              <button
                type="button"
                onClick={() => onClear(track.id)}
                disabled={track.notes.length === 0}
                aria-label={`Clear ${inst.name} lane`}
                className="w-14 shrink-0 rounded-lg border border-zinc-800 text-xs font-medium text-zinc-500 hover:text-zinc-300 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-zinc-500"
              >
                clear
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
